import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { usePopper } from 'react-popper';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $getSelection, $isRangeSelection } from 'lexical';
import { $isLinkNode, TOGGLE_LINK_COMMAND, LinkNode } from '@lexical/link';
import { mergeRegister } from '@lexical/utils';

import { getSelectedNode } from '../../utils';
import { useSetting } from '../../hooks';

export type Props = {
  link?: boolean;
};

const FormatLink: React.FC<Props> = ({ link }) => {
  const [editor] = useLexicalComposerContext();
  const [isLink, setIsLink] = useState<boolean>(false);

  const inputRef = useRef<HTMLInputElement | null>(null);
  const [trigger, setTrigger] = useState<HTMLElement | null>(null);
  const popperElement = useRef<HTMLDivElement | null>(null);
  const customModifier = useMemo(
    () => ({
      name: 'preventOverflow',
      enabled: true,
      options: {
        tether: false,
        boundary: document.querySelector('.iva-editor-wrapper')!,
      },
    }),
    [],
  );

  const { styles, attributes } = usePopper(trigger, popperElement.current, {
    modifiers: [customModifier],
    placement: 'bottom-start',
  });

  const insertLink = useCallback(() => {
    editor.update(() => {
      const selection = $getSelection();
      if (!$isRangeSelection(selection)) return;
      if (selection.getTextContent() === '' && !isLink) return;

      if (!isLink) {
        editor.dispatchCommand(TOGGLE_LINK_COMMAND, 'https://');
      } else {
        editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
      }
    });
  }, [editor, isLink]);

  const editLink = useCallback(() => {
    editor.update(() => {
      editor.dispatchCommand(
        TOGGLE_LINK_COMMAND,
        inputRef.current?.value || '',
      );
      const selection = $getSelection();
      if (!$isRangeSelection(selection)) return;

      const node = getSelectedNode(selection);
      const isNodeLink = $isLinkNode(node);
      let linkNode: LinkNode;

      if (isNodeLink) {
        linkNode = node;
      } else {
        linkNode = node.getParent() as LinkNode;
      }

      const nextNode = linkNode.getNextSiblings()[0];

      if (!nextNode) {
        return;
      }

      selection.anchor.set(nextNode.getKey(), 0, 'text');
      selection.focus.set(nextNode.getKey(), 0, 'text');
    });
  }, [editor]);

  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(({ editorState, dirtyElements }) => {
        editorState.read(() => {
          if (!link) return;

          const selection = $getSelection();
          if (!$isRangeSelection(selection)) return;

          const node = getSelectedNode(selection);
          const isNodeLink = $isLinkNode(node);
          const isNodeParentLink = $isLinkNode(node.getParent());
          setIsLink(isNodeLink || isNodeParentLink);

          if (isNodeLink) {
            setTrigger(editor.getElementByKey(node.getKey()));
            inputRef.current!.value = (node as LinkNode).getURL();
          } else if (isNodeParentLink) {
            setTrigger(editor.getElementByKey(node.getParent()!.getKey()));
            inputRef.current!.value = (node.getParent() as LinkNode).getURL();
          } else {
            setTrigger(
              editor.getElementByKey(node.getParent()?.getKey() || ''),
            );
            inputRef.current!.value = '';
          }
        });
      }),
      editor.registerMutationListener(LinkNode, (map) => {
        const transformKey = Array.from(map.keys())[0];
        setTrigger(editor.getElementByKey(transformKey));
      }),
    );
  }, [editor, link]);

  const { editable } = useSetting();

  return link ? (
    <React.Fragment>
      <button
        onClick={insertLink}
        className={'toolbar-item spaced ' + (isLink ? 'active' : '')}
        aria-label="Insert Link"
        disabled={!editable}
      >
        <i className="format link" />
      </button>

      <div
        ref={popperElement}
        style={{
          ...styles.popper,
          zIndex: attributes.popper?.['data-popper-reference-hidden']
            ? -1
            : trigger?.tagName === 'A'
            ? 1
            : -1,
        }}
        {...attributes.popper}
        className="popper"
      >
        <div className="d-flex-between">
          <div className="ui input">
            <input
              type="text"
              ref={inputRef}
              style={{ width: 300 }}
              onChange={(e) => {
                inputRef.current!.value = e.target.value;
              }}
            />
          </div>
          <a
            className="mx-8"
            target="_blank"
            rel="noreferrer"
            href={inputRef.current?.value}
          >
            Visit URL
          </a>
          <a className="mx-8 cursor-pointer" onClick={editLink}>
            Save
          </a>
        </div>
      </div>
    </React.Fragment>
  ) : null;
};

export default FormatLink;
